import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, PieChart as PieChartIcon, Users } from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6', '#a855f7', '#f97316'];
const fmt = (n) => Math.round(parseFloat(n) || 0).toLocaleString('fr-FR');

export default function RepartitionCapital({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/investisseurs`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  const totalMises = items.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0);
  const chartData = items
    .filter((i) => (parseFloat(i.mise) || 0) > 0)
    .map((i) => ({
      id: i.id,
      name: `${i.prenom || ''} ${i.nom || ''}`.trim() || 'Investisseur',
      value: parseFloat(i.mise) || 0,
      email: i.email || '-',
      pourcentage: totalMises > 0 ? ((parseFloat(i.mise) || 0) / totalMises) * 100 : 0,
    }));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="repartition-capital-page">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="gap-2"
            data-testid="repartition-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <PieChartIcon className="h-7 w-7 text-indigo-600" /> Répartition du Capital
            </h1>
            <p className="text-gray-600 mt-1 text-sm">
              Détail du capital investi par chaque investisseur
            </p>
          </div>
        </div>

        {/* Summary */}
        <Card className="border-l-4 border-l-indigo-500 bg-indigo-50">
          <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-indigo-700 font-semibold uppercase">Capital total investi</p>
              <p className="text-3xl font-bold text-indigo-800" data-testid="repartition-total">
                {fmt(totalMises)} €
              </p>
            </div>
            <div className="flex items-center gap-2 text-indigo-800">
              <Users className="h-5 w-5" />
              <span className="font-semibold">{chartData.length} investisseur(s)</span>
            </div>
          </CardContent>
        </Card>

        {/* Pie chart + Table */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Pie */}
          <Card className="shadow-lg lg:col-span-3">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
              <CardTitle className="flex items-center gap-2">
                <PieChartIcon className="h-5 w-5" /> Diagramme des parts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 bg-white">
              {chartData.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-gray-400 italic text-sm">
                  Aucun investisseur actif avec une mise
                </div>
              ) : (
                <div style={{ width: '100%', height: 380 }} data-testid="repartition-pie">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={130}
                        label={(entry) => `${entry.name} (${entry.pourcentage.toFixed(1)}%)`}
                        labelLine={false}
                      >
                        {chartData.map((entry, idx) => (
                          <Cell key={entry.id} fill={PALETTE[idx % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name, item) =>
                          [`${fmt(value)} € (${item.payload.pourcentage.toFixed(2)}%)`, name]
                        }
                      />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="shadow-lg lg:col-span-2">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" /> Détail par investisseur
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-purple-200 bg-purple-50 text-purple-900">
                      <th className="px-3 py-2 text-left font-semibold">Nom</th>
                      <th className="px-3 py-2 text-right font-semibold">Mise</th>
                      <th className="px-3 py-2 text-right font-semibold">Part</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-3 py-6 text-center text-gray-400 italic">
                          Aucun investisseur
                        </td>
                      </tr>
                    ) : (
                      chartData.map((d, idx) => (
                        <tr
                          key={d.id}
                          className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50`}
                          data-testid={`repartition-row-${d.id}`}
                        >
                          <td className="px-3 py-2 flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-full"
                              style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}
                            />
                            <span className="font-medium">{d.name}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-indigo-700 whitespace-nowrap">
                            {fmt(d.value)} €
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-purple-700 whitespace-nowrap">
                            {d.pourcentage.toFixed(2)} %
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {chartData.length > 0 && (
                    <tfoot>
                      <tr className="bg-indigo-100 border-t-2 border-indigo-300">
                        <td className="px-3 py-3 font-bold text-gray-800">TOTAL</td>
                        <td className="px-3 py-3 text-right font-bold text-indigo-700">
                          {fmt(totalMises)} €
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-purple-700">100 %</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
